import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowUpRight, Download, MapPin, Calendar, Users } from "lucide-react";
import BrochureDialog from "@/components/BrochureDialog";
import { trackEvent, metaTrack } from "@/lib/analytics";

const PROGRAMS = {
    "cps-2026": {
        name: "Climate Power Summit",
        year: "2026",
        tag: "Global Summit",
        headline: "People • Planet • Progress.",
        subheadline: "Jaipur, India · 13–15 November 2026",
        img: "https://images.unsplash.com/photo-1757310062384-d3bcfe3026e8?w=1600&q=80&fm=jpg&fit=crop",
        brochure: "https://customer-assets-v7afamib.emergentagent.net/job_f4c7dbec-80a4-4d4b-81c9-de1d792e1fe8/artifacts/yudn4mgt_CPS%20.pdf",
        summary: "Climate Power Summit 2026 is an international, action-oriented platform advancing climate leadership through education, policy dialogue, innovation and cross-sector collaboration — hosted by Z Ed School of Global Impact in Jaipur, India.",
        opportunities: [
            { count: "10", label: "Fully Funded" },
            { count: "40", label: "Partially Funded" },
            { count: "20", label: "Self-Funded" },
        ],
        pillars: [
            { t: "Climate Leadership", d: "Empowering leaders across institutions to drive climate action." },
            { t: "Policy & Governance", d: "Frameworks and just transitions for a warming world." },
            { t: "Sustainability & ESG", d: "Responsible finance, circular economy, ESG integration." },
            { t: "Innovation & Green Tech", d: "Scalable green solutions and startup showcases." },
            { t: "Education & Youth", d: "The next generation, at the table where decisions are made." },
            { t: "Global Partnerships", d: "Cross-border coalitions turning ideas into collective impact." },
        ],
        flow: [
            { d: "Day 1", t: "Leadership & Dialogue", items: ["Opening Ceremony", "Keynote Sessions", "Panel Discussions", "Global Climate Crisis Council", "Cultural Evening"] },
            { d: "Day 2", t: "Action & Solutions", items: ["Youth-Led Panel", "Solution Showcase", "Commitments & Action Plan", "Awards & Closing Ceremony"] },
            { d: "Day 3", t: "Optional", items: ["Technical Visits", "Institutional Interactions", "Jaipur City Experience"] },
        ],
        meta: [
            { icon: MapPin, label: "Jaipur, India" },
            { icon: Calendar, label: "13–15 November 2026" },
            { icon: Users, label: "Multi-stakeholder" },
        ],
    },
};

export default function ProgramPage() {
    const { slug } = useParams();
    const p = PROGRAMS[slug];
    const [brochure, setBrochure] = useState(null);
    const [sfStatus, setSfStatus] = useState(null);

    // CPS_Landing_Page_View — also fires when a visitor lands on
    // /programs/cps-2026 (secondary entry point into the CPS funnel).
    // Meta standard ViewContent fires alongside, dedup-guarded per tab.
    useEffect(() => {
        if (slug === "cps-2026") {
            trackEvent("CPS_Landing_Page_View", { source: "program_page", program_slug: slug });
            metaTrack("ViewContent", {
                content_ids: ["cps-2026"],
                content_name: "Climate Power Summit 2026 — Programme",
                content_category: "cps-2026-application",
                content_type: "product",
            }, { dedupKey: "ViewContent:cps-2026-program" });
            // Advertised self-funded capacity follows the admin-configured
            // slot settings (never a hardcoded number).
            api.get("/cps/self-funded/status")
                .then(({ data }) => setSfStatus(data))
                .catch(() => setSfStatus(null));
        }
    }, [slug]);

    // Self-funded tier count: configured capacity when set, "Open" when
    // uncapped, "Closed" when the admin has closed the category.
    const tierCount = (op) => {
        if (op.label !== "Self-Funded" || slug !== "cps-2026" || !sfStatus) return op.count;
        if (!sfStatus.admin_open) return "Closed";
        if (sfStatus.max_seats != null) return String(sfStatus.max_seats);
        return "Open";
    };

    if (!p) {
        return (
            <div className="pt-40 pb-40 text-center">
                <h1 className="serif-display text-4xl">Program not found</h1>
                <Link to="/" className="btn-ghost mt-8 inline-flex">Return home</Link>
            </div>
        );
    }

    return (
        <div data-testid={`program-page-${slug}`}>
            {/* Program hero */}
            <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 relative">
                    <div className="label-mono">— {p.tag.toUpperCase()} · {p.year}</div>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
                        className="serif-display text-[clamp(2.6rem,8vw,7.5rem)] leading-[0.9] mt-4"
                    >
                        {p.name}<span className="text-[var(--gold)]">.</span>
                    </motion.h1>
                    <p className="mt-8 text-xl md:text-2xl serif-italic text-[var(--gold)] max-w-3xl">{p.headline}</p>
                    {p.subheadline && (
                        <p className="mt-3 label-mono text-[var(--ivory)]/70" data-testid="program-subheadline">
                            {p.subheadline}
                        </p>
                    )}

                    <div className="mt-8 flex flex-wrap items-center gap-6">
                        {p.meta.map((m, i) => {
                            const Icon = m.icon;
                            return (
                                <div key={i} className="label-mono flex items-center gap-2 text-[var(--ivory)]/70">
                                    <Icon className="w-3.5 h-3.5" /> {m.label}
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-10 flex flex-wrap gap-3">
                        <Link
                            to={`/apply/${slug}`}
                            onClick={() => slug === "cps-2026" && trackEvent("CPS_Apply_Click", { source: "program_page_top" })}
                            className="btn-gold"
                            data-testid="program-apply-btn"
                        >
                            Apply Now <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <button type="button" onClick={() => setBrochure({ url: p.brochure, program_slug: slug, program_name: `${p.name} ${p.year}` })} className="btn-ghost" data-testid="program-brochure-btn">
                            Download Brochure <Download className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* Editorial image */}
            <section className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
                <div className="max-w-[1440px] mx-auto spotlight-frame aspect-[21/9]">
                    <img src={p.img} alt={p.name} className="w-full h-full object-cover" />
                </div>
            </section>

            {/* Summary */}
            <section className="section-ivory py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="label-mono">— OVERVIEW</div>
                    </div>
                    <div className="md:col-span-8">
                        <p className="serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-[var(--navy)]">
                            {p.summary}
                        </p>
                    </div>
                </div>
            </section>

            {/* Opportunities (only for programs that define them, e.g. CPS 2026) */}
            {p.opportunities && (
                <section className="py-20 md:py-28 bg-[#0F2A22]" data-testid="program-opportunities">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
                            <div className="md:col-span-5">
                                <div className="label-mono" style={{ color: "#A7C957" }}>— APPLICATION SEATS</div>
                                <h3 className="serif-display text-3xl md:text-5xl mt-4" style={{ color: "#F5EFD8" }}>
                                    70 leaders. <span className="serif-italic" style={{ color: "#A7C957" }}>Three funding tiers.</span>
                                </h3>
                            </div>
                            <div className="md:col-span-7 md:pl-8">
                                <p style={{ color: "rgba(245,239,216,0.72)" }} className="text-base md:text-lg leading-relaxed">
                                    Funding category is assigned after application review — apply first, be considered for all three tiers.
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 border" style={{ borderColor: "rgba(245,239,216,0.18)" }}>
                            {p.opportunities.map((op, i) => (
                                <div
                                    key={op.label}
                                    className={`p-6 md:p-10 ${i < 2 ? "border-r" : ""}`}
                                    style={{ borderColor: "rgba(245,239,216,0.18)" }}
                                    data-testid={`program-opp-${op.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div className="serif-display text-5xl md:text-7xl" style={{ color: "#A7C957" }}>{tierCount(op)}</div>
                                    <div className="label-mono text-[10px] md:text-xs mt-2" style={{ color: "rgba(245,239,216,0.72)" }}>{op.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Two registration pathways — CPS 2026 only */}
            {slug === "cps-2026" && (
                <section className="py-20 md:py-28 bg-[#0F2A22] border-t" style={{ borderColor: "rgba(245,239,216,0.18)" }} data-testid="program-pathways">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                        <div className="label-mono" style={{ color: "#A7C957" }} data-testid="sf-banner-eyebrow">— SELF-FUNDED REGISTRATIONS NOW OPEN</div>
                        <h3 className="serif-display text-3xl md:text-5xl mt-4 max-w-3xl" style={{ color: "#F5EFD8" }}>
                            Two ways to join <span className="serif-italic" style={{ color: "#A7C957" }}>CPS 2026.</span>
                        </h3>
                        <div className="grid md:grid-cols-2 gap-8 mt-12">
                            {/* Pathway B — Self-funded direct registration */}
                            <div className="p-8 md:p-10 border" style={{ borderColor: "rgba(167,201,87,0.45)", background: "rgba(167,201,87,0.07)" }} data-testid="sf-pathway-card">
                                <div className="label-mono" style={{ color: "#A7C957" }}>SELF-FUNDED DELEGATE</div>
                                <div className="serif-display text-5xl md:text-6xl mt-4" style={{ color: "#F5EFD8" }}>
                                    ₹32,000 <span className="text-2xl">INR</span>
                                </div>
                                <p className="label-mono text-[11px] mt-2" style={{ color: "rgba(245,239,216,0.72)" }}>
                                    Climate Power Summit 2026 · 13–15 November 2026 · Jaipur, India
                                </p>
                                <p className="mt-5 serif-italic text-lg" style={{ color: "#A7C957" }}>
                                    Three Days of Climate Leadership, Global Networking and Meaningful Action.
                                </p>
                                <ul className="mt-6 space-y-3" style={{ color: "rgba(245,239,216,0.72)" }}>
                                    {[
                                        "Three nights shared accommodation (13–16 Nov)",
                                        "Selected meals per the official itinerary",
                                        "Summit conference access, panels & workshops",
                                        "Day 3 organised sustainability activities",
                                        "Participation certificate & delegate kit",
                                    ].map((item) => (
                                        <li key={item} className="flex items-start gap-3">
                                            <span className="w-1.5 h-1.5 mt-2" style={{ background: "#A7C957" }} />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/apply/cps-2026/self-funded"
                                    onClick={() => trackEvent("CPS_SelfFunded_Click", { source: "program_page" })}
                                    className="mt-8 inline-flex items-center gap-2 px-6 py-4 label-mono text-[11px] font-medium transition-all hover:scale-[1.02]"
                                    style={{ background: "#A7C957", color: "#0F2A22" }}
                                    data-testid="sf-register-pay-btn"
                                >
                                    Register &amp; Pay ₹32,000 <ArrowUpRight className="w-4 h-4" />
                                </Link>
                                <p className="mt-4 label-mono text-[10px]" style={{ color: "rgba(245,239,216,0.5)" }}>
                                    Direct registration · no interview · no separate application fee · non-refundable under the published CPS policy
                                </p>
                            </div>
                            {/* Pathway A — Scholarship application (existing) */}
                            <div className="p-8 md:p-10 border" style={{ borderColor: "rgba(245,239,216,0.18)" }} data-testid="scholarship-pathway-card">
                                <div className="label-mono" style={{ color: "#A7C957" }}>SCHOLARSHIP APPLICATION</div>
                                <div className="serif-display text-3xl md:text-4xl mt-4" style={{ color: "#F5EFD8" }}>
                                    Apply for a funded seat
                                </div>
                                <p className="mt-5" style={{ color: "rgba(245,239,216,0.72)" }}>
                                    Application → application fee → review → interview → selection → scholarship offer →
                                    remaining payment, if applicable. Funding category is assigned after review.
                                </p>
                                <Link
                                    to="/apply/cps-2026"
                                    onClick={() => trackEvent("CPS_Apply_Click", { source: "program_page_pathways" })}
                                    className="mt-8 inline-flex items-center gap-2 px-6 py-4 label-mono text-[11px] border transition-colors hover:bg-[rgba(245,239,216,0.06)]"
                                    style={{ borderColor: "rgba(245,239,216,0.35)", color: "#F5EFD8" }}
                                    data-testid="scholarship-apply-btn"
                                >
                                    Continue with scholarship application <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Pillars */}
            <section className="py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="label-mono mb-10">— WHAT YOU'LL EXPERIENCE</div>
                    <div className="grid md:grid-cols-3 border-t border-l border-[rgba(255,255,240,0.12)]">
                        {p.pillars.map((pl, i) => (
                            <motion.div
                                key={pl.t}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                                className="p-8 md:p-10 border-r border-b border-[rgba(255,255,240,0.12)]"
                            >
                                <div className="label-mono text-[var(--gold)]">/ 0{i + 1}</div>
                                <h3 className="serif-display text-2xl md:text-3xl mt-3">{pl.t}</h3>
                                <p className="mt-3 text-[var(--ivory)]/70 leading-relaxed">{pl.d}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Flow */}
            <section className="section-ivory py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="label-mono mb-10">— TENTATIVE FLOW</div>
                    <div className="grid md:grid-cols-3 gap-0 border-t border-b border-l border-[rgba(11,30,63,0.2)]">
                        {p.flow.map((f, i) => (
                            <div key={i} className="p-8 md:p-10 border-r border-[rgba(11,30,63,0.2)]">
                                <div className="label-mono text-[color:#8F6F26]">{f.d}</div>
                                <h3 className="serif-display text-2xl md:text-3xl mt-3 text-[var(--navy)]">{f.t}</h3>
                                <ul className="mt-6 space-y-2 text-[var(--navy)]/80">
                                    {f.items.map((it) => (
                                        <li key={it} className="flex items-start gap-2">
                                            <span className="w-1.5 h-1.5 bg-[var(--emerald)] mt-2" />
                                            {it}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* End CTA */}
            <section className="py-24 md:py-32 bg-[var(--navy-deep)]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 text-center">
                    <div className="label-mono">— YOUR TURN</div>
                    <h2 className="serif-display text-4xl md:text-6xl lg:text-7xl mt-6">
                        Reserve your seat in <span className="serif-italic text-[var(--gold)]">2026.</span>
                    </h2>
                    <div className="mt-10 flex flex-wrap gap-3 justify-center">
                        <Link
                            to={`/apply/${slug}`}
                            onClick={() => slug === "cps-2026" && trackEvent("CPS_Apply_Click", { source: "program_page_bottom" })}
                            className="btn-gold"
                            data-testid="program-end-apply"
                        >
                            Apply Now <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <button type="button" onClick={() => setBrochure({ url: p.brochure, program_slug: slug, program_name: `${p.name} ${p.year}` })} className="btn-ghost">
                            Download Brochure <Download className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>
            <BrochureDialog brochure={brochure} onClose={() => setBrochure(null)} />
        </div>
    );
}
